export { toZodEnum } from './zod'

/**
 * Creates a type where exactly one of the specified keys must be present and defined,
 * while all others must be undefined or omitted.
 *
 * @description Use this type when you need to ensure that exactly one property from a set
 * of properties is provided. This is useful for exclusive options, discriminated unions,
 * or when you want to prevent conflicting properties from being set simultaneously.
 *
 * @example
 * ```typescript
 * interface PaymentMethod {
 *   creditCard?: string
 *   paypal?: string
 *   bankTransfer?: string
 * }
 *
 * type SinglePaymentMethod = OneOf<PaymentMethod>
 *
 * // ✅ Valid - exactly one method
 * const payment1: SinglePaymentMethod = { creditCard: '1234' }
 * const payment2: SinglePaymentMethod = { paypal: 'user@example.com' }
 *
 * // ❌ Invalid - multiple methods specified
 * // const payment3: SinglePaymentMethod = { creditCard: '1234', paypal: 'user@example.com' }
 * ```
 *
 * @template T - The base type containing the properties
 * @template Keys - The keys from T that should be exclusive (defaults to all keys of T)
 */
export type OneOf<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Record<Exclude<Keys, K>, undefined>>
  }[Keys]

/**
 * Makes all properties of a type nullable (allows `null` instead of `undefined`).
 * Similar to TypeScript's `Partial<T>`, but uses `null` instead of making properties optional.
 *
 * @description This utility type is useful when working with APIs or databases that
 * represent missing values as `null` rather than `undefined`, or when you want to
 * explicitly handle nullable state in your application.
 *
 * @example
 * ```typescript
 * interface User {
 *   id: number
 *   name: string
 *   email: string
 * }
 *
 * type NullableUser = Nullable<User>
 * // Result: { id: number | null; name: string | null; email: string | null }
 *
 * const user: NullableUser = {
 *   id: 1,
 *   name: null, // ✅ Valid - can be null
 *   email: 'john@example.com'
 * }
 * ```
 *
 * @template T - The base type to make nullable
 */
export type Nullable<T> = {
  [P in keyof T]: T[P] | null
}

/**
 * Creates a type with all `undefined` and `null` values removed from properties.
 * Makes all properties required and non-nullable.
 *
 * @description This utility type is particularly useful for cleaning up API response
 * types or ensuring that after runtime validation, your types reflect that certain
 * values are guaranteed to be defined.
 *
 * @example
 * ```typescript
 * interface ApiResponse {
 *   data: string | null
 *   error: string | undefined
 *   loading: boolean
 * }
 *
 * type CleanResponse = RemoveUndefinedAndNullKeys<ApiResponse>
 * // Result: { data: string; error: string; loading: boolean }
 *
 * // Use with the runtime function
 * const rawResponse: ApiResponse = { data: 'hello', error: undefined, loading: false }
 * const cleanedResponse = removeUndefinedAndNullKeys(rawResponse)
 * // cleanedResponse is now typed as CleanResponse
 * ```
 *
 * @template T - The base type to clean up
 */
export type RemoveUndefinedAndNullKeys<T> = {
  [K in keyof T]-?: Exclude<T[K], undefined | null>
}

/**
 * Makes all optional properties explicitly required to be either their type or `undefined`.
 * This ensures that all object properties are accounted for and prevents accidentally
 * omitting optional properties.
 *
 * @description Use this type when you want to ensure all properties are explicitly handled,
 * even optional ones. This is useful for configuration objects, API payloads, or anywhere
 * you want to be explicit about what values are provided vs. intentionally omitted.
 *
 * @example
 * ```typescript
 * interface Config {
 *   host: string
 *   port?: number
 *   ssl?: boolean
 * }
 *
 * type ExplicitConfig = AllKeys<Config>
 * // Result: { host: string; port: number | undefined; ssl: boolean | undefined }
 *
 * // ❌ Will not compile - missing optional properties
 * const config1: ExplicitConfig = {
 *   host: 'localhost'
 *   // Missing port and ssl
 * }
 *
 * // ✅ Will compile - all properties explicit
 * const config2: ExplicitConfig = {
 *   host: 'localhost',
 *   port: undefined,  // Must be explicit
 *   ssl: undefined    // Must be explicit
 * }
 * ```
 *
 * @template T - The base type to make properties explicit
 */
type AllOptionalKeys<T> = { [K in keyof T]-?: undefined extends T[K] ? K : never }[keyof T]
type AllNonOptionalKeys<T> = { [K in keyof T]-?: undefined extends T[K] ? never : K }[keyof T]
export type AllKeys<T> = { [K in AllOptionalKeys<T>]: T[K] | undefined } & { [K in AllNonOptionalKeys<T>]: T[K] }

/**
 * Filters object keys based on whether their values extend a specific condition type.
 * Returns a mapped type where matching keys are preserved and non-matching keys become `never`.
 *
 * @description This utility type is useful for extracting keys from an object type based on
 * the types of their values. Common use cases include finding all string properties,
 * all optional properties, or all properties of a specific interface type.
 *
 * @example
 * ```typescript
 * interface Mixed {
 *   id: number
 *   name: string
 *   isActive: boolean
 *   count: number
 *   description: string
 * }
 *
 * // Get keys that have string values
 * type StringKeys = FilterCondition<Mixed, string>
 * // Result: { id: never; name: 'name'; isActive: never; count: never; description: 'description' }
 *
 * // Extract just the key names
 * type StringKeyNames = FilterCondition<Mixed, string>[keyof Mixed]
 * // Result: 'name' | 'description'
 *
 * // Get keys that have number values
 * type NumberKeys = FilterCondition<Mixed, number>[keyof Mixed]
 * // Result: 'id' | 'count'
 * ```
 *
 * @template Base - The object type to filter keys from
 * @template Condition - The type condition to match against
 */
export type FilterCondition<Base, Condition> = {
  [Key in keyof Base]: Base[Key] extends Condition ? Key : never
}

/**
 * Runtime function that removes all properties with `undefined` or `null` values
 * from an object, modifying the original object in place.
 *
 * @description This function provides runtime cleanup of objects by removing properties
 * that have null or undefined values. It modifies the original object and returns it
 * with updated TypeScript typing to reflect the cleaned state.
 *
 * @example
 * ```typescript
 * const obj = {
 *   name: 'John',
 *   age: null,
 *   email: undefined,
 *   city: 'Vienna'
 * }
 *
 * const cleaned = removeUndefinedAndNullKeys(obj)
 * // Result: { name: 'John', city: 'Vienna' }
 * // Type: { name: string; city: string }
 *
 * console.log(cleaned.name);  // ✅ TypeScript knows this exists
 * console.log(cleaned.age);   // ❌ TypeScript error - property doesn't exist
 * ```
 *
 * @param o - The object to clean up
 * @returns The same object with null/undefined properties removed and updated typing
 * @template T - The input object type
 */
export const removeUndefinedAndNullKeys = <T>(o: T) => {
  for (const k in o) {
    if (o[k] === undefined || o[k] === null) delete o[k]
  }
  return o as RemoveUndefinedAndNullKeys<T>
}

/**
 * Extracts the element type from a readonly array or tuple type.
 *
 * @description This utility type is useful when you need to work with the element type
 * of an array without knowing the specific array type. It works with both mutable arrays,
 * readonly arrays, and tuple types.
 *
 * @example
 * ```typescript
 * const fruits = ['apple', 'banana', 'orange'] as const
 * type Fruit = ElementType<typeof fruits>
 * // Result: 'apple' | 'banana' | 'orange'
 *
 * type StringArray = string[]
 * type StringElement = ElementType<StringArray>
 * // Result: string
 *
 * type ReadonlyNumbers = readonly number[]
 * type NumberElement = ElementType<ReadonlyNumbers>
 * // Result: number
 *
 * // Useful for generic functions
 * function processArray<T extends ReadonlyArray<unknown>>(
 *   arr: T,
 *   processor: (item: ElementType<T>) => void
 * ) {
 *   arr.forEach(processor)
 * }
 * ```
 *
 * @template T - The array type to extract elements from
 */
// eslint-disable-next-line @typescript-eslint/no-shadow
export type ElementType<T extends ReadonlyArray<unknown>> =
  T extends ReadonlyArray<infer ElementType> ? ElementType : never

/**
 * Makes all properties of an object optional recursively, including nested objects.
 * Unlike TypeScript's built-in `Partial<T>`, this applies the partial transformation
 * to all nested levels.
 *
 * @description This utility type is useful for configuration objects, update payloads,
 * or any scenario where you want to make deeply nested structures optional.
 * It recursively traverses object properties and makes them all optional.
 *
 * @example
 * ```typescript
 * interface User {
 *   id: number
 *   profile: {
 *     name: string
 *     address: {
 *       street: string
 *       city: string
 *       country: string
 *     }
 *   }
 *   settings: {
 *     theme: string
 *     notifications: boolean
 *   }
 * }
 *
 * type PartialUser = RecursivePartial<User>
 * // All properties at all levels become optional
 *
 * const user: PartialUser = {
 *   profile: {
 *     address: {
 *       city: 'Vienna'
 *       // street and country are optional
 *     }
 *     // name is optional
 *   }
 *   // id and settings are optional
 * }
 * ```
 *
 * @template T - The type to make recursively partial
 */
export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends Record<string, unknown> ? RecursivePartial<T[P]> : T[P]
}

/**
 * Makes all properties optional except for the specified keys, which remain required.
 * Creates a type where you must provide at least the specified properties.
 *
 * @description This utility type is useful when you want to require certain key properties
 * while making others optional. Common use cases include update operations, partial forms,
 * or API endpoints that require minimum information.
 *
 * @example
 * ```typescript
 * interface User {
 *   id: number
 *   name: string
 *   email: string
 *   phone: string
 *   address: string
 * }
 *
 * // Require at least email, make others optional
 * type UserWithRequiredEmail = AtLeast<User, 'email'>
 *
 * // ✅ Valid - has required email
 * const user1: UserWithRequiredEmail = {
 *   email: 'john@example.com'
 *   // id, name, phone, address are optional
 * }
 *
 * // ✅ Valid - has required email plus optional fields
 * const user2: UserWithRequiredEmail = {
 *   email: 'jane@example.com',
 *   name: 'Jane',
 *   phone: '123-456-7890'
 * }
 *
 * // ❌ Invalid - missing required email
 * // const user3: UserWithRequiredEmail = { name: 'Bob' }
 * ```
 *
 * @template T - The base type
 * @template K - The keys that must be required
 */
export type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>

/**
 * Ensures that at least one property of the object type is present and defined.
 * Creates a union type where each variant requires exactly one property while making others optional.
 *
 * @description This utility type is useful for scenarios where you need at least one piece of
 * information from a set of options, such as contact methods, search criteria, or identification fields.
 *
 * @example
 * ```typescript
 * interface ContactInfo {
 *   email?: string
 *   phone?: string
 *   address?: string
 * }
 *
 * type RequiredContact = AtLeastOne<ContactInfo>
 *
 * // ✅ Valid - has at least one contact method
 * const contact1: RequiredContact = { email: 'john@example.com' }
 * const contact2: RequiredContact = { phone: '123456' }
 * const contact3: RequiredContact = { phone: '123456', address: 'Vienna' }
 *
 * // ❌ Invalid - no properties provided
 * // const contact4: RequiredContact = {}
 *
 * // Example with search criteria
 * interface SearchCriteria {
 *   name?: string
 *   category?: string
 *   priceRange?: [number, number]
 * }
 *
 * type ValidSearch = AtLeastOne<SearchCriteria>
 * // Must provide at least one search criterion
 * ```
 *
 * @template T - The base type where at least one property is required
 */
export type AtLeastOne<T> = { [K in keyof T]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<keyof T, K>>> }[keyof T]

/**
 * Excludes `undefined` from a type, ensuring the value is defined.
 *
 * @description This utility type is useful when you want to ensure that a value is not undefined,
 * particularly after runtime checks or in generic functions where you need to narrow types.
 *
 * @example
 * ```typescript
 * type MaybeString = string | undefined
 * type DefiniteString = Defined<MaybeString>
 * // Result: string
 *
 * function processValue<T>(value: T | undefined): Defined<T> | null {
 *   if (value === undefined) {
 *     return null
 *   }
 *   return value; // Type is now T (undefined excluded)
 * }
 *
 * // Usage with optional properties
 * interface User {
 *   id: string
 *   name?: string
 * }
 *
 * function getDefinedName(user: User): Defined<User['name']> | null {
 *   return user.name ?? null; // Returns string | null, not string | undefined | null
 * }
 * ```
 *
 * @template T - The type to exclude undefined from
 */
export type Defined<T> = T extends undefined ? never : T

/**
 * Exhaustiveness checking function for discriminated unions and switch statements.
 * Throws an error if called, indicating that a case was not handled.
 *
 * @description This function is used to ensure compile-time exhaustiveness checking
 * in TypeScript. When used as the default case in a switch statement or the else
 * branch of a conditional, TypeScript will produce a compile error if not all cases
 * are handled. At runtime, if this function is ever called, it means a new case
 * was added but not handled in the code.
 *
 * @example
 * ```typescript
 * type Status = 'pending' | 'approved' | 'rejected'
 *
 * function handleStatus(status: Status): string {
 *   switch (status) {
 *     case 'pending':
 *       return 'Processing...'
 *     case 'approved':
 *       return 'Approved!'
 *     case 'rejected':
 *       return 'Rejected!'
 *     default:
 *       return assertNever(status); // Ensures all cases are handled
 *   }
 * }
 *
 * // If you add a new status like 'cancelled', TypeScript will error
 * // until you handle it in the switch statement
 *
 * // Also useful in if-else chains
 * function processEvent(event: 'click' | 'hover' | 'focus') {
 *   if (event === 'click') {
 *     // handle click
 *   } else if (event === 'hover') {
 *     // handle hover
 *   } else if (event === 'focus') {
 *     // handle focus
 *   } else {
 *     assertNever(event); // Compile error if new event type added
 *   }
 * }
 * ```
 *
 * @param value - Should be of type `never` if all cases are handled
 * @throws Error with the unexpected value
 * @returns Never returns (always throws)
 */
export const assertNever = (value: never): never => {
  throw new Error(`Unexpected value: ${JSON.stringify(value)}`)
}

type Falsy = undefined | null | false | 0 | ''

/**
 * Type-safe assertion function that throws an error if the value is falsy,
 * otherwise returns the value with falsy types excluded from the type.
 *
 * @description This function provides runtime validation with compile-time type safety.
 * It's useful for asserting that values exist when you expect them to, and it updates
 * the TypeScript type to reflect that falsy values have been excluded.
 *
 * @example
 * ```typescript
 * function processUser(user?: User) {
 *   const validUser = assert(user, 'User is required')
 *   // validUser is now typed as User (undefined excluded)
 *   console.log(validUser.name); // No TypeScript error
 * }
 *
 * const maybeString: string | null = getValue()
 * const definiteString = assert(maybeString, 'String cannot be null')
 * // definiteString is typed as string (null excluded)
 *
 * // Works with any falsy values
 * const maybeNumber: number | 0 = getNumber()
 * const nonZeroNumber = assert(maybeNumber, 'Number must be non-zero')
 * // nonZeroNumber is typed as number (0 excluded)
 *
 * // Common pattern with API responses
 * const apiResponse = await fetchUser()
 * const user = assert(apiResponse.data, 'Failed to fetch user data')
 * // user is guaranteed to exist and be typed correctly
 * ```
 *
 * @param value - The value to check for truthiness
 * @param message - Custom error message (defaults to 'Unexpected value: ')
 * @throws Error if the value is falsy
 * @returns The value with falsy types excluded
 * @template T - The input value type
 */
export const assert = <T>(value: T | Falsy, message: string = 'Unexpected value: ') => {
  if (!value) {
    throw new Error(`${message} ${JSON.stringify(value)}`)
  }
  return value as Exclude<T, Falsy>
}

/**
 * Creates a new object with specified keys removed. Unlike Lodash's omit,
 * this preserves exact TypeScript typing and is type-safe.
 *
 * @description This function creates a shallow copy of the input object and removes
 * the specified keys. It's useful for data sanitization, creating public API responses,
 * or removing sensitive information from objects.
 *
 * @example
 * ```typescript
 * interface User {
 *   id: number
 *   name: string
 *   email: string
 *   password: string
 *   internalNotes: string
 * }
 *
 * const user: User = {
 *   id: 1,
 *   name: 'John',
 *   email: 'john@example.com',
 *   password: 'secret123',
 *   internalNotes: 'VIP customer'
 * }
 *
 * const publicUser = omit(user, ['password', 'internalNotes'])
 * // Type: { id: number; name: string; email: string }
 * // Result: { id: 1, name: 'John', email: 'john@example.com' }
 *
 * // Useful for API responses
 * function getUserProfile(user: User) {
 *   return omit(user, ['password']); // Safe to return
 * }
 *
 * // Works with any object type
 * const config = { host: 'localhost', port: 3000, debug: true, secret: 'key' }
 * const publicConfig = omit(config, ['secret'])
 * // { host: 'localhost', port: 3000, debug: true }
 * ```
 *
 * @param data - The object to omit keys from
 * @param keys - Array of keys to remove from the object
 * @returns A new object with specified keys removed
 * @template Data - The input object type
 * @template Keys - The keys to omit (must be keys of Data)
 */
export const omit = <Data extends object, Keys extends keyof Data>(data: Data, keys: Array<Keys>): Omit<Data, Keys> => {
  const result = { ...data }
  for (const key of keys) {
    delete result[key]
  }
  return result as Omit<Data, Keys>
}

/**
 * Represents any valid JSON value type, including primitives, arrays, and objects.
 * This type recursively defines all possible JSON-serializable values.
 *
 * @description This utility type is useful when working with JSON APIs, configuration files,
 * or any data that needs to be JSON-serializable. It ensures type safety while allowing
 * the flexibility of working with dynamic JSON data.
 *
 * @example
 * ```typescript
 * function parseJsonData(jsonString: string): JsonValue {
 *   return JSON.parse(jsonString)
 * }
 *
 * function processApiResponse(data: JsonValue) {
 *   // data can be any valid JSON value
 *   if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
 *     // data is a JSON object
 *     console.log('Processing object:', data)
 *   }
 * }
 *
 * const validJsonData: JsonValue = {
 *   name: 'John',
 *   age: 30,
 *   hobbies: ['reading', 'coding'],
 *   address: {
 *     city: 'Vienna',
 *     country: 'Austria'
 *   },
 *   isActive: true,
 *   lastLogin: null
 * }
 *
 * // Useful for API payloads
 * interface ApiRequest {
 *   method: string
 *   data: JsonValue
 *   headers?: Record<string, string>
 * }
 * ```
 */
export type JsonValue = string | number | boolean | null | Array<JsonValue> | { [key: string]: JsonValue }
